import { nil, UITextView, UIView, YES } from "uicore-ts"
import { RHColor } from "../RHColor"
import { RHAltModeManager, RHAltModeRegistrant } from "./RHAltModeManager"


/**
 * Conforming classes (RHBarButton, RHButton, RHBarLinkButton) can intercept
 * the setter and supply a different `displayView` for the badge without losing
 * the clean public API.
 *
 * Ideally this would be a mixin/trait that also carries the implementation,
 * but TypeScript's interface system does not support that. Each conforming
 * class must copy the same three-line implementation of `_setAltModeKey`.
 */
export interface RHAltModeKeyable {
    
    /** Backing handler instance — `undefined` when no key is assigned. */
    _altModeKeyHandler?: RHAltModeKeyHandler
    
    /**
     * The shortcut letter shown as a badge in alt-mode (e.g. `"A"`).
     * Set to `undefined` to remove the shortcut.
     */
    altModeKey?: string
    
    /**
     * Creates or destroys the handler. `displayView` is the view that will
     * host the badge — pass `undefined` to use the button itself, or pass a
     * wrapper view (as `RHBarLinkButton` does) to position the badge correctly
     * over the full clickable area.
     */
    _setAltModeKey(key: string | undefined, displayView: UIView | undefined): void
    
}


// MARK: - RHAltModeBadgeView

/**
 * The small letter badge drawn over a button during alt-mode. Positions
 * itself in the top-right corner of its superview by overriding
 * `calculateAndSetViewFrame`, so it always reads fresh bounds regardless of
 * when it is added to the view tree.
 */
class RHAltModeBadgeView extends UITextView {
    
    static readonly badgeSize = 18
    
    override calculateAndSetViewFrame() {
        if (!this.superview) {
            return
        }
        const badgeSize = RHAltModeBadgeView.badgeSize
        this.frame = this.superview.bounds
            .rectangleWithHeight(badgeSize)
            .rectangleWithWidth(badgeSize, 1)
    }
    
}


// MARK: - RHAltModeKeyHandler

/**
 * Owned by one button view. Registers that button for a single shortcut letter
 * with `RHAltModeManager`, draws the keyboard badge when alt-mode is active,
 * and fires the button's activation event when the letter is pressed.
 *
 * The handler silently skips badge display and key activation when its
 * `_displayView` is not currently in the view tree. This allows multiple
 * buttons to share the same key letter (e.g. login and logout both using "L")
 * without any manual registration juggling — only the visible one reacts.
 *
 * Usage — set on any RHAltModeKeyable button:
 *
 *   button.altModeKey = "A"
 *
 * Setting `altModeKey` to `undefined` removes the handler and deregisters it.
 */
export class RHAltModeKeyHandler implements RHAltModeRegistrant {
    
    /** The single uppercase letter this handler responds to. */
    readonly altKey: string
    
    /** The view whose activation event will be fired. */
    _button: UIView
    
    /**
     * The view that will receive the badge as a subview and have
     * `overflow: visible` applied. For plain RHBarButton this is the button
     * itself. For buttons wrapped in RHBarLinkButton the caller should pass the
     * link-button wrapper so the badge sits on top of the wrapper frame.
     */
    _displayView: UIView
    
    _badge?: RHAltModeBadgeView
    _registered = false
    
    
    constructor(key: string, button: UIView, displayView?: UIView) {
        this.altKey = key.toUpperCase()
        this._button = button
        this._displayView = displayView ?? button
        
        this._registered = RHAltModeManager.shared.registerRegistrant(this)
    }
    
    
    // MARK: - RHAltModeRegistrant
    
    get isInViewTree(): boolean {
        return this._displayView.superview != undefined
    }
    
    
    enterAltMode() {
        if (!this._registered || !this.isInViewTree) {
            return
        }
        this._showBadge()
    }
    
    
    exitAltMode() {
        this._hideBadge()
    }
    
    
    handleAltKey(key: string): boolean {
        if (key.toUpperCase() !== this.altKey || !this.isInViewTree) {
            return false
        }
        this._button.sendControlEventForKey(UIView.controlEvent.EnterDown, nil)
        return YES
    }
    
    
    // MARK: - Lifecycle
    
    /**
     * Call this when the handler is no longer needed (e.g. `altModeKey` is
     * cleared or the button is deallocated). Hides any visible badge and
     * removes this handler from the manager.
     */
    deregister() {
        this._hideBadge()
        RHAltModeManager.shared.deregisterRegistrant(this)
        this._registered = false
    }
    
    
    // MARK: - Badge
    
    private _showBadge() {
        
        if (this._badge) {
            return
        }
        
        // Allow the badge to escape the display view's clip rect, and also
        // the superview's clip rect (e.g. the top-bar container clips buttons).
        this._displayView.style.overflow = "visible"
        if (this._displayView.superview) {
            this._displayView.superview.style.overflow = "visible"
        }
        
        this._badge = new RHAltModeBadgeView().configuredWithObject({
            text: this.altKey,
            textColor: RHColor.textWhite,
            backgroundColor: RHColor.textBlack.colorWithAlpha(0.8),
            style: {
                borderRadius: "3px",
                padding: "1px 4px",
                fontSize: "11px",
                fontWeight: "700",
                fontFamily: "monospace",
                lineHeight: "1",
                whiteSpace: "nowrap",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                textAlign: UITextView.textAlignment.center,
                left: "0px",
                top: "0px",
                zIndex: "100"
            }
        }).addedAsSubviewToView(this._displayView)
        
    }
    
    
    private _hideBadge() {
        if (!this._badge) {
            return
        }
        this._displayView.style.overflow = ""
        if (this._displayView.superview) {
            this._displayView.superview.style.overflow = ""
        }
        this._badge.removeFromSuperview()
        this._badge = undefined
    }
    
}
